import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import useToggle from 'hooks/useToggle'

type Props = {
  heading: string
  subHeading?: string
  price?: string
  showBorder?: boolean
  image?: App.Image
  imgUrl?: string
  imgStyle?: 'logo' | 'photo'
  disabled?: boolean
  onSelect?: (checked: boolean) => void
}

const Root = styled(Clickable)`
  display: flex;
  gap: ${rem(12)};
  align-items: center;
  width: 100%;

  &.is-disabled {
    opacity: 0.5;
  }

  &.package-container {
    display: flex;
    align-items: flex-start;
    flex-direction: row;
    gap: ${rem(12)};

    .package-items {
      flex-direction: column;
    }
  }

  > .click-disabled {
    pointer-events: none;
  }
`

const BookingCardContainer = styled.div`
  width: 100%;
  min-width: 0;

  &.with-border {
    background: ${(props) => props.theme.palette.neutral.surface.normalBackground};
    border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${(props) => props.theme.borderRadius.L};
    padding: ${rem(12)};
  }
`

const BookingCardDetails = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  min-width: 0;
`

const TextContent = styled.div`
  flex: 1;
  min-width: 0;
`

const PriceText = styled(BodyText)`
  white-space: nowrap;
  flex-shrink: 0;
`

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  &.logo {
    object-fit: contain;
  }
`

const ImgContainer = styled.div`
  width: ${rem(60)};
  height: ${rem(60)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: ${(props) => props.theme.borderRadius.L};
  overflow: hidden;

  &.logo {
    background: ${(props) => props.theme.palette.neutral.surface.lightBackground};
    padding: ${rem(8)};
  }
`

const BookingItemImage = styled(ResponsiveImage)`
  border-radius: ${(props) => props.theme.borderRadius.L};
  width: ${rem(60)};
  flex-shrink: 0;
`

const ImagePlaceHolder = styled.div`
  min-height: ${rem(60)};
  min-width: ${rem(60)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${(props) => props.theme.borderRadius.L};
`

function BookingItemCard(props: Props) {
  const {
    heading,
    subHeading,
    price,
    image,
    showBorder = true,
    imgUrl,
    imgStyle = 'photo',
    disabled = false,
    onSelect,
  } = props
  const intl = useIntl()
  const { value, toggle } = useToggle(false)
  const checkBoxRef = useRef<HTMLInputElement>(null)

  const onClick = useCallback(() => {
    if (disabled) {
      return
    }

    toggle()
    onSelect?.(!value)
  }, [disabled, onSelect, toggle, value])

  return (
    <Root className={clsx({ selected: value, 'is-disabled': disabled })} onClick={onClick} disabled={disabled}>
      {showBorder && (
        <CircleCheckboxInput
          className="click-disabled"
          ref={checkBoxRef}
          checked={value}
          disabled={disabled}
        />
      )}
      <BookingCardContainer
        className={clsx('booking-card-container', { 'with-border': showBorder })}
      >
        <BookingCardDetails>
          {image?.id && (
            <BookingItemImage
              aria-hidden="true"
              alt={intl.formatMessage({
                id: 'JEfoOEzZTngI3YbN91O7',
                defaultMessage: 'Package preview',
                description: 'Accessibility alt text for booking item package preview image',
              })}
              id={image.id}
              mobileAspectRatio="1:1"
              width={60}
            />
          )}
          {!image && imgUrl && (
            <ImgContainer className={imgStyle}>
              <Img alt={heading} src={imgUrl} className={imgStyle} />
            </ImgContainer>
          )}
          {!image && !imgUrl && <ImagePlaceHolder />}
          <TextContent>
            {showBorder && (
              <Heading lineClamp={2} variant="heading6">
                {heading}
              </Heading>
            )}
            {!showBorder && (
              <BodyText lineClamp={2} variant="medium" colour="normal-contrast">
                {heading}
              </BodyText>
            )}
            {subHeading && (
              <BodyText lineClamp={1} variant="small" colour="light-foreground-alt">
                {subHeading}
              </BodyText>
            )}
          </TextContent>
          {!!price && (
            <PriceText variant="medium" colour="normal-contrast">
              {price}
            </PriceText>
          )}
        </BookingCardDetails>
      </BookingCardContainer>
    </Root>
  )
}

export default BookingItemCard
